import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Send, Loader2 } from 'lucide-react';
import { siteConfig } from '../data/mockData';
import { trackFormSubmit, trackPhoneClick, trackEmailClick } from '../lib/analytics';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        // Track form submission
        trackFormSubmit('Contact Form');
        
        toast.success('Message envoyé !', {
          description: result.message,
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
        });
      } else {
        throw new Error(result.detail || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Erreur', {
        description: 'Une erreur est survenue lors de l\'envoi du message. Veuillez réessayer ou nous contacter par téléphone.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-amber-50 to-neutral-50 py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-5xl font-bold text-neutral-900 mb-6">
              Contacter votre infirmière à domicile
            </h1>
            <p className="text-xl text-neutral-600 leading-relaxed">
              Vous avez besoin de soins infirmiers à domicile ? Contactez-moi par téléphone, 
              email ou via le formulaire ci-dessous. Je vous répondrai dans les plus brefs délais.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="border-neutral-200">
                <CardHeader>
                  <CardTitle className="text-xl">Coordonnées</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <a
                    href={`tel:${siteConfig.phone}`}
                    onClick={trackPhoneClick}
                    className="flex items-start space-x-3 p-3 rounded-lg hover:bg-amber-50 transition-colors group"
                  >
                    <div className="bg-amber-100 p-2 rounded-lg group-hover:bg-amber-200 transition-colors">
                      <Phone className="h-5 w-5 text-amber-700" />
                    </div>
                    <div>
                      <p className="font-medium text-neutral-900">Téléphone</p>
                      <p className="text-neutral-600">{siteConfig.phone}</p>
                    </div>
                  </a>

                  <a
                    href={`mailto:${siteConfig.email}`}
                    onClick={trackEmailClick}
                    className="flex items-start space-x-3 p-3 rounded-lg hover:bg-amber-50 transition-colors group"
                  >
                    <div className="bg-amber-100 p-2 rounded-lg group-hover:bg-amber-200 transition-colors">
                      <Mail className="h-5 w-5 text-amber-700" />
                    </div>
                    <div>
                      <p className="font-medium text-neutral-900">Email</p>
                      <p className="text-neutral-600 break-all">{siteConfig.email}</p>
                    </div>
                  </a>

                  <div className="flex items-start space-x-3 p-3">
                    <div className="bg-amber-100 p-2 rounded-lg">
                      <MapPin className="h-5 w-5 text-amber-700" />
                    </div>
                    <div>
                      <p className="font-medium text-neutral-900">Zone d'intervention</p>
                      <p className="text-neutral-600">{siteConfig.address}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-white">
                <CardContent className="pt-6">
                  <p className="text-sm text-neutral-700 leading-relaxed">
                    <strong className="text-neutral-900">Urgences :</strong> En cas d'urgence vitale, 
                    composez le <strong>112</strong>. Pour les soins urgents, contactez-moi directement 
                    par téléphone.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-neutral-200 shadow-lg">
                <CardHeader className="bg-gradient-to-r from-amber-50 to-neutral-50">
                  <CardTitle className="text-2xl">Formulaire de contact</CardTitle>
                  <p className="text-neutral-600 mt-2">
                    Remplissez ce formulaire et je vous recontacterai dans les plus brefs délais
                  </p>
                </CardHeader>
                
                <CardContent className="pt-6">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Name */}
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-neutral-900">
                        Nom complet <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="border-neutral-300 focus:border-amber-500 focus:ring-amber-500"
                        placeholder="Votre nom et prénom"
                      />
                    </div>

                    {/* Email */}
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-neutral-900">
                        Adresse email <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="border-neutral-300 focus:border-amber-500 focus:ring-amber-500"
                        placeholder="votre.email@exemple.be"
                      />
                    </div>

                    {/* Phone */}
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-neutral-900">
                        Numéro de téléphone <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        className="border-neutral-300 focus:border-amber-500 focus:ring-amber-500"
                        placeholder="+32 XXX XX XX XX"
                      />
                    </div>

                    {/* Subject */}
                    <div className="space-y-2">
                      <Label htmlFor="subject" className="text-neutral-900">
                        Objet de la demande <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="subject"
                        name="subject"
                        type="text"
                        required
                        value={formData.subject}
                        onChange={handleChange}
                        className="border-neutral-300 focus:border-amber-500 focus:ring-amber-500"
                        placeholder="Ex: Demande de soins de plaies"
                      />
                    </div>

                    {/* Message */}
                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-neutral-900">
                        Message <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        rows={6}
                        className="border-neutral-300 focus:border-amber-500 focus:ring-amber-500 resize-none"
                        placeholder="Décrivez votre besoin en soins infirmiers, vos disponibilités, etc."
                      />
                    </div>

                    {/* Privacy Notice */}
                    <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                      <p className="text-sm text-neutral-700 leading-relaxed">
                        <strong className="text-neutral-900">Confidentialité :</strong> Vos données personnelles 
                        sont protégées et utilisées uniquement pour traiter votre demande de soins. 
                        Conformément au RGPD et au secret professionnel médical, elles ne seront jamais 
                        transmises à des tiers.
                      </p>
                    </div>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          <Send className="h-5 w-5 mr-2" />
                          Envoyer le message
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-neutral-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-neutral-900 mb-6">
                Une réponse rapide à votre demande
              </h2>
              <p className="text-lg text-neutral-700 leading-relaxed mb-4">
                Je m'engage à répondre à toutes les demandes de contact dans un délai maximum de 24 heures 
                (jours ouvrables). Pour les urgences, privilégiez le contact téléphonique qui permet une 
                prise en charge immédiate.
              </p>
              <p className="text-neutral-600 leading-relaxed">
                Lors de notre premier échange, nous pourrons discuter de vos besoins spécifiques en soins, 
                vérifier votre prescription médicale, et organiser les premières visites à votre domicile.
              </p>
            </div>
            
            <div>
              <img
                src="https://images.pexels.com/photos/7551641/pexels-photo-7551641.jpeg"
                alt="Consultation soins infirmiers à domicile"
                className="rounded-2xl shadow-xl w-full object-cover"
                style={{ aspectRatio: '4/3' }}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
